/**
 * DashboardToolbar — Sport tabs, market type tabs, date navigator, and game filter.
 *
 * Extracted from ValueDashboardView to reduce view file size.
 * Renders as the leftContent slot of DashboardHeader.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { getTodayStr } from '@/lib/infra/dateUtils';
import { type MarketType, SPORT_CONFIGS } from '@/lib/sportsDiscovery';
import type { Sport } from '@/lib/sportsDiscovery';
import type { GameFilterMode } from '@/hooks/ui/useGameSlate';

/** Sport tab options */
const SPORT_TABS = SPORT_CONFIGS.map((config) => ({
  value: config.sport,
  label: config.shortLabel,
}));

/** Market type labels */
const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  moneyline: 'Moneyline',
  spread: 'Spread',
  total: 'Total',
};

interface DashboardToolbarProps {
  selectedSport: Sport;
  selectedMarketType: MarketType;
  availableMarketTypes: MarketType[];
  selectedDate: string | null;
  availableDates: string[];
  gameFilter: GameFilterMode;
  onSportChange: (sport: Sport) => void;
  onMarketTypeChange: (type: MarketType) => void;
  onDateChange: (date: string | null) => void;
  onDatePrev: () => void;
  onDateNext: () => void;
  onDateToday: () => void;
  onDateAll: () => void;
  onGameFilterChange: (filter: GameFilterMode) => void;
}

export function DashboardToolbar({
  selectedSport,
  selectedMarketType,
  availableMarketTypes,
  selectedDate,
  availableDates,
  gameFilter,
  onSportChange,
  onMarketTypeChange,
  onDateChange,
  onDatePrev,
  onDateNext,
  onDateToday,
  onDateAll,
  onGameFilterChange,
}: DashboardToolbarProps) {
  return (
    <>
      {/* Sport tabs */}
      <div className="bg-muted flex rounded-lg p-1">
        {SPORT_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => onSportChange(tab.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              selectedSport === tab.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Market type tabs */}
      <div className="bg-muted flex rounded-lg p-1">
        {availableMarketTypes.map((type) => (
          <button
            key={type}
            onClick={() => onMarketTypeChange(type)}
            className={cn(
              'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
              selectedMarketType === type
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {MARKET_TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {/* Date navigator */}
      <div className="border-border flex items-center gap-1 rounded border px-1 py-0.5">
        <button
          onClick={onDatePrev}
          disabled={!selectedDate || availableDates.indexOf(selectedDate) <= 0}
          className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors disabled:opacity-30"
          title="Previous date"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <input
          type="date"
          value={selectedDate ?? ''}
          onChange={(e) => onDateChange(e.target.value || null)}
          className="text-foreground w-[7rem] cursor-pointer border-none bg-transparent px-1 py-0.5 text-xs font-medium outline-none [color-scheme:dark]"
          title="Click to pick a date"
        />
        <button
          onClick={onDateNext}
          disabled={
            !selectedDate || availableDates.indexOf(selectedDate) >= availableDates.length - 1
          }
          className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors disabled:opacity-30"
          title="Next date"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={onDateToday}
          className={cn(
            'rounded px-2 py-0.5 text-xs font-medium transition-colors',
            selectedDate === getTodayStr()
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
          title="Jump to today"
        >
          Today
        </button>
        <button
          onClick={onDateAll}
          className={cn(
            'rounded px-2 py-0.5 text-xs font-medium transition-colors',
            selectedDate === null
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
          title="Show all dates"
        >
          All
        </button>
        <span className="bg-border mx-1 inline-block h-4 w-px" />
        {(['active', 'all', 'settled'] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => onGameFilterChange(mode)}
            className={cn(
              'rounded px-2 py-0.5 text-xs font-medium transition-colors',
              gameFilter === mode
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title={
              mode === 'active'
                ? 'Show only active/upcoming games'
                : mode === 'all'
                  ? 'Show all games'
                  : 'Show only settled/completed games'
            }
          >
            {mode === 'active' ? 'Active' : mode === 'all' ? 'All' : 'Settled'}
          </button>
        ))}
      </div>
    </>
  );
}
